<template>
  <div class="flex flex-col h-full">
    <div class="p-3 border-b border-gray-200 dark:border-gray-300">
      <div class="flex items-center justify-between">
        <span class="text-xs font-semibold uppercase tracking-wider text-gray-500">Projects</span>
        <button @click="showNew = !showNew" class="text-teal-500 hover:text-teal-700 text-lg leading-none" title="New project">+</button>
      </div>

      <!-- New project form -->
      <div v-if="showNew" class="mt-2 p-2 bg-teal-50 dark:bg-gray-200 border border-teal-200 dark:border-gray-300 rounded-md space-y-1.5">
        <input ref="newInput" v-model="newForm.name" @keydown.escape="showNew = false"
          placeholder="name" maxlength="50"
          class="w-full px-2 py-1 text-xs border border-gray-300 rounded bg-white dark:bg-gray-50 focus:outline-none focus:border-teal-500" />
        <input v-model="newForm.display_label" placeholder="Display Label"
          class="w-full px-2 py-1 text-xs border border-gray-300 rounded bg-white dark:bg-gray-50 focus:outline-none focus:border-teal-500" />
        <input v-model="newForm.color" placeholder="#0d9488"
          class="w-full px-2 py-1 text-xs border border-gray-300 rounded bg-white dark:bg-gray-50 focus:outline-none focus:border-teal-500" />
        <div class="flex gap-2">
          <button @click="showNew = false" class="text-xs text-gray-500 hover:text-gray-700">Cancel</button>
          <button @click="createProject" :disabled="!newForm.name.trim()" class="text-xs text-teal-600 hover:text-teal-800 font-medium">Create</button>
        </div>
      </div>
    </div>

    <div class="flex-1 overflow-y-auto">
      <!-- All Tasks -->
      <button @click="$emit('select', null)"
        class="w-full text-left px-3 py-1.5 text-sm flex items-center justify-between hover:bg-gray-100 dark:hover:bg-gray-200"
        :class="{ 'bg-teal-50 text-teal-700 font-medium': !currentProjectId }">
        <span>All Tasks</span>
      </button>

      <!-- Projects -->
      <div v-for="project in sortedProjects" :key="project.id" class="group">
        <!-- Edit form -->
        <div v-if="editingId === project.id" class="px-2 py-1.5 space-y-1 bg-gray-50 dark:bg-gray-200">
          <input v-model="editForm.name" placeholder="name" maxlength="50"
            class="w-full px-1.5 py-0.5 text-xs border border-gray-300 rounded bg-white focus:outline-none focus:border-teal-500" />
          <input v-model="editForm.display_label" placeholder="Display Label"
            class="w-full px-1.5 py-0.5 text-xs border border-gray-300 rounded bg-white focus:outline-none focus:border-teal-500" />
          <input v-model="editForm.color" placeholder="#color"
            class="w-full px-1.5 py-0.5 text-xs border border-gray-300 rounded bg-white focus:outline-none focus:border-teal-500" />
          <div class="flex gap-2">
            <button @click="editingId = null" class="text-[10px] text-gray-500">Cancel</button>
            <button @click="saveEdit(project.id)" class="text-[10px] text-teal-600 font-medium">Save</button>
            <button @click="$emit('delete', project.id)" class="text-[10px] text-red-400 hover:text-red-600 ml-auto">Delete</button>
          </div>
        </div>

        <!-- Display -->
        <button v-else @click="$emit('select', project.id)"
          class="w-full text-left px-3 py-1.5 text-sm flex items-center justify-between hover:bg-gray-100 dark:hover:bg-gray-200"
          :class="{ 'bg-teal-50 text-teal-700 font-medium': currentProjectId === project.id }">
          <span class="flex items-center gap-2 min-w-0">
            <span v-if="project.color" class="w-2.5 h-2.5 rounded-full flex-shrink-0" :style="{ background: project.color }"></span>
            <span class="truncate">{{ project.display_label || project.name }}</span>
          </span>
          <span class="flex items-center gap-1 flex-shrink-0">
            <span class="text-xs text-gray-400">{{ project.task_count }}</span>
            <button @click.stop="startEdit(project)" class="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-gray-600 p-0.5" title="Edit">
              <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"/>
              </svg>
            </button>
          </span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, nextTick, watch } from 'vue'

const props = defineProps({
  projects: Array,
  currentProjectId: [Number, null],
})

const sortedProjects = computed(() =>
  [...(props.projects || [])].sort((a, b) =>
    (a.display_label || a.name).localeCompare(b.display_label || b.name)
  )
)

const emit = defineEmits(['select', 'create', 'update', 'delete'])

const showNew = ref(false)
const newForm = reactive({ name: '', display_label: '', color: '' })
const newInput = ref(null)

const editingId = ref(null)
const editForm = reactive({ name: '', display_label: '', color: '' })

watch(showNew, (val) => {
  if (val) nextTick(() => newInput.value?.focus())
})

function createProject() {
  if (!newForm.name.trim()) return
  const attrs = { name: newForm.name.trim() }
  if (newForm.display_label.trim()) attrs.display_label = newForm.display_label.trim()
  if (newForm.color.trim()) attrs.color = newForm.color.trim()
  emit('create', attrs)
  newForm.name = ''
  newForm.display_label = ''
  newForm.color = ''
  showNew.value = false
}

function startEdit(project) {
  editingId.value = project.id
  editForm.name = project.name
  editForm.display_label = project.display_label || ''
  editForm.color = project.color || ''
}

function saveEdit(id) {
  const attrs = {}
  if (editForm.name.trim()) attrs.name = editForm.name.trim()
  if (editForm.display_label !== undefined) attrs.display_label = editForm.display_label.trim() || null
  if (editForm.color !== undefined) attrs.color = editForm.color.trim() || null
  emit('update', id, attrs)
  editingId.value = null
}
</script>
